import './painting-flow-item-edit.css';

import {
  IonChip,
  IonCol,
  IonGrid,
  IonIcon,
  IonInput,
  IonListHeader,
  IonRow,
  IonText,
} from '@ionic/react';
import { Form } from 'formik';
import { createOutline } from 'ionicons/icons';
import { cloneDeep, isEmpty } from 'lodash';
import { ReactElement, useCallback, useMemo, useState } from 'react';
import { RightAlignedButtons } from '../../../../../../components/button/aligned-buttons';
import { CloseButton } from '../../../../../../components/button/close-button';
import { FormSubmitButton } from '../../../../../../components/form/form-submit.button';
import { IntercomHelp } from '../../../../../../components/help/intercom-help';
import { FieldLocked } from '../../../../../../components/ui/field-locked';
import { getLocalizedText } from '../../../../../../i18n/i18n.utils';
import { blurOnEnter } from '../../../../../../util/keyboard';
import { logger } from '../../../../../../util/logger';
import { FlowItemEditProps } from '../../../../shared/component/flow-item/edit/flow-item-edit.types';
import { FlowItemFormComponent } from '../../../../shared/component/flow-item/edit/flow-item-form';
import { useFlowItemFormContext } from '../../../../shared/component/flow-item/edit/flow-item-form.context';
import { FlowSectionEditActionBar } from '../../../../shared/component/flow-item/edit/flow-section-edit-action-bar';
import { ParametersList } from '../../../../shared/component/flow-item-parameter/parameter-list';
import { WaitForOptions } from '../../../../shared/component/teach/option/wait-for-options';
import { useFlowServices } from '../../../../shared/hook/flow-service.hooks';
import {
  ExecutionInfo,
  ExecutionInfoState,
} from '../../../../shared/hook/live-execution.hooks';
import {
  Flow,
  FlowItem,
  FlowItemForm,
  SystemUnit,
} from '../../../../shared/model/flow.types';
import { FlowItemService } from '../../../../shared/service/service.types';
import { BeaconPaintingData } from '../../model/beacon-painting-data';
import { PaintProcessSettingsForm } from './paint-process-settings-form';
import { PaintingFlowItemEditActionBar } from './painting-flow-item-edit-action-bar';

const PaintingFlowItemEdit = (
  props: FlowItemEditProps
): ReactElement | null => {
  const {
    item,
    onUpdateItem,
    onDeleteItem,
    readOnly,
    system,
    setStartAtId,
    startAtId,
    onClosed,
    executionInfo,
    itemNamePrefix: prefix,
  } = props;

  const { flow, flowItemService } = useFlowServices();
  const [saving, setSaving] = useState<boolean>(false);

  const onSubmit = async (item: FlowItem) => {
    setSaving(true);
    logger.info('saving flow item changes from panel');
    await onUpdateItem(item);
    setSaving(false);
  };

  const itemRef = item?.ref;

  const itemMetadata = useMemo(() => {
    if (itemRef) {
      return flowItemService.getItemMetadata(itemRef);
    }
  }, [flowItemService, itemRef]);

  if (item) {
    return (
      <FlowItemFormComponent onSubmit={onSubmit} item={item}>
        {({ dirty, errors, values, setFieldValue }) => {
          const isReadyToSubmit = dirty && isEmpty(errors);
          return (
            <div className={'flow-panel-container'}>
              <Form>
                <>
                  <IonListHeader className={'panel-header'}>
                    <IonGrid class="ion-no-padding no-padding">
                      <IonRow class="ion-align-items-center">
                        <IonCol className="flex flex-grow-0">
                          {readOnly ? (
                            <FieldLocked show={readOnly ?? false} />
                          ) : (
                            <IonIcon icon={createOutline} />
                          )}
                        </IonCol>
                        <IonCol className="flex-grow-1">
                          <IonRow class="ion-align-items-center">
                            {!isEmpty(prefix) && (
                              <IonCol className="flex-grow-0">
                                <IonText className="prefix-text">
                                  {prefix} -{' '}
                                </IonText>
                              </IonCol>
                            )}
                            <IonCol className="flex-grow-1">
                              <IonInput
                                readonly={readOnly}
                                value={values.name}
                                onIonInput={(e) => {
                                  setFieldValue('name', e.detail.value);
                                }}
                                onKeyPress={blurOnEnter}
                                enterkeyhint="done"
                                className="name-input"
                              />
                            </IonCol>
                          </IonRow>
                        </IonCol>
                        {executionInfo?.state ===
                          ExecutionInfoState.PLAYING && (
                          <IonCol className="flex-grow-0">
                            <IonChip color="success" disabled>
                              {getLocalizedText('common:executing')}
                            </IonChip>
                          </IonCol>
                        )}
                        {itemMetadata?.articleId && (
                          <IonCol>
                            <IntercomHelp articleId={itemMetadata?.articleId} />
                          </IonCol>
                        )}
                      </IonRow>
                    </IonGrid>
                  </IonListHeader>
                  <div className="header-divider" />
                  <PaintingFlowItemDetail
                    flow={flow}
                    flowItemService={flowItemService}
                    item={item}
                    system={system}
                    readOnly={readOnly}
                    setStartAtId={setStartAtId}
                    startAtId={startAtId}
                    executionInfo={executionInfo}
                    onDelete={onDeleteItem}
                    onClosed={onClosed}
                  />
                </>
                <RightAlignedButtons>
                  <CloseButton
                    onClose={() => {
                      if (typeof onClosed === 'function') {
                        onClosed();
                      }
                    }}
                  />
                  <FormSubmitButton
                    loading={saving}
                    buttonProps={{
                      color: isReadyToSubmit ? 'primary' : 'medium',
                      disabled: !isReadyToSubmit || saving,
                      fill: isReadyToSubmit ? 'solid' : 'outline',
                    }}
                    placement="none"
                  >
                    {getLocalizedText('common:save')}
                  </FormSubmitButton>
                </RightAlignedButtons>
              </Form>
            </div>
          );
        }}
      </FlowItemFormComponent>
    );
  }

  return null;
};

interface PaintingFlowItemDetailProps {
  flow: Flow;
  flowItemService: FlowItemService;
  item: FlowItem;
  system: SystemUnit;
  readOnly?: boolean;
  setStartAtId?: (id: string, enableStepMode?: boolean) => Promise<void>;
  startAtId?: string;
  executionInfo?: ExecutionInfo;
  onDelete: (items: FlowItem[]) => Promise<void>;
  onClosed?: () => void;
}

const PaintingFlowItemDetail = (
  props: PaintingFlowItemDetailProps
): ReactElement => {
  const {
    flow,
    flowItemService,
    item,
    system,
    readOnly,
    setStartAtId,
    startAtId,
    executionInfo,
    onDelete,
    onClosed,
  } = props;
  const parameters = flowItemService.getParametersMetadata(item.ref);

  const { setValidationSchema } = useFlowItemFormContext();

  const clonedItems = useMemo(() => {
    return cloneDeep(flow.items);
  }, [flow.items]);

  const recalculateParameterValues = useCallback(
    (form: FlowItemForm) => {
      // find index of item
      const index = clonedItems.findIndex((i) => i.id === form.id);

      if (index === -1) {
        return form;
      }

      const item = flowItemService.convertFormToFlowItem(form, system);

      clonedItems[index] = item;

      flowItemService.recalculateParameterValues(flow.config, clonedItems);

      return flowItemService.convertFlowItemToForm(item, system);
    },
    [flow.config, flowItemService, clonedItems, system]
  );

  let component: ReactElement;
  let actionBar: ReactElement = (
    <PaintingFlowItemEditActionBar
      items={[item]}
      onDelete={onDelete}
      readOnly={readOnly}
      setStartAtId={setStartAtId}
      startAtId={startAtId}
      executionInfo={executionInfo}
    />
  );

  switch (item.ref) {
    case BeaconPaintingData.FlowItemRef.PAINT:
      component = (
        <PaintProcessSettingsForm
          system={system}
          readOnly={readOnly}
          setValidationSchema={setValidationSchema}
          recalculateParameterValues={recalculateParameterValues}
        />
      );
      break;
    case BeaconPaintingData.FlowItemRef.WAIT_FOR:
      component = <WaitForOptions />;
      break;
    case BeaconPaintingData.FlowItemRef.SECTION:
      actionBar = (
        <FlowSectionEditActionBar
          sectionItem={item}
          flowItems={flow.items}
          readOnly={readOnly}
          onDelete={onDelete}
          onClosed={onClosed}
        />
      );
      component = (
        <ParametersList
          parameters={parameters}
          system={system}
          readOnly={readOnly}
        />
      );
      break;
    default:
      component = (
        <ParametersList
          parameters={parameters}
          system={system}
          readOnly={readOnly}
        />
      );
  }

  return (
    <>
      {actionBar}
      {component}
    </>
  );
};

export { PaintingFlowItemEdit };
